import { Control } from 'react-hook-form';
import Input from 'components/Common/Input';
import TelInput from 'components/Common/TelInput';
import { Box, Typography, Button, Paper, CircularProgress, useTheme } from '@mui/material';
import { FaChevronRight } from 'react-icons/fa';
import { matchIsValidTel } from 'mui-tel-input';

interface StepProps {
  onNext: () => void | Promise<void>;
  control: Control<any>;
  isSubmitting?: boolean;
  disabledFields?: {
    primary_contact_phone?: boolean;
    primary_contact_email?: boolean;
  };
}

const ContactStep: React.FC<StepProps> = ({ onNext, control, isSubmitting = false, disabledFields }: StepProps) => {
  const theme = useTheme();

  return (
    <Paper sx={{ p: 3, borderRadius: 3, width: '100%' }}>
      <Typography fontWeight={600} sx={{ fontSize: { xs: '16px', md: '24px' } }}>
        Who should we contact?
      </Typography>

      <Typography lineHeight='normal' fontSize='14px' color='text.secondary' mt={1}>
        Our account manager will use these details to send updates and schedule your kickoff call.
      </Typography>

      <Box display='flex' gap={2} mt={3} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
        <Input name='firstName' control={control} fullWidth label='First Name' />
        <Input name='lastName' control={control} fullWidth label='Last Name' />
      </Box>

      <Input
        name='primary_contact_email'
        control={control}
        fullWidth
        required
        rules={{ required: 'Email is required' }}
        label='Email'
        placeholder='yourmail@gmail.com'
        margin='normal'
        sx={{ mt: 2 }}
        disabled={disabledFields?.primary_contact_email}
      />

      <Box mt={2}>
        <TelInput
          name='primary_contact_phone'
          label='Primary Contact Phone'
          control={control}
          required
          rules={{
            required: 'Phone number is required',
            validate: (value: string) => matchIsValidTel(value || '') || 'Enter a valid phone number'
          }}
          defaultCountry='US'
          fullWidth
          disabled={disabledFields?.primary_contact_phone}
        />
        <Typography fontSize='12px' color='text.secondary' mt={0.5}>
          Include country code, e.g. +14168282835
        </Typography>
      </Box>

      <Button
        variant='contained'
        fullWidth
        disabled={isSubmitting}
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          gap: 1,
          background: theme.palette.primary.main
        }}
        onClick={onNext}
      >
        {isSubmitting ? (
          <CircularProgress size={20} color='inherit' />
        ) : (
          <>
            Start Now <FaChevronRight />
          </>
        )}
      </Button>

      <Typography mt={2} lineHeight='normal' fontSize='12px' color='text.secondary' textAlign='center'>
        Left midway? No worries — enter the same email and phone to pick up where you left off.
      </Typography>
    </Paper>
  );
};

export default ContactStep;
